/**
 * RestatementLogPanel — Restatement history for audit trail
 *
 * Phase 4.5 — redesign-p4-analacct-45b
 * Displays restatement history from existing API.
 * Shows date, author, engine affected, delta, and reason.
 */

import React, { useEffect, useState } from 'react';
import { History, RefreshCw, User, FileText, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BRAND_NAME } from '@/config/brand';

// Import UI primitives
import { DataTable, Column } from '@/components/ui/DataTable';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';

// Import API
import { api } from '@/utils/api';

export interface RestatementLogPanelProps {
  reportId: string;
  reportData: any;
}

/**
 * Raw shape returned by `GET /api/v1/restatement-log/{report_id}` — mirrors
 * `RestatementResponse` in `backend/app/api/v1/restatement_log.py`. The
 * endpoint returns a BARE LIST of these (not `{ entries: [...] }`), and there
 * is no `engine_id` on the model — restatements are keyed by `field`, not by
 * engine.
 */
interface RestatementLogEntryDTO {
  id: string;
  report_id: string;
  date: string;
  field: string;
  original_value: number;
  restated_value: number;
  reason: string;
  approved_by: string;
  is_active: boolean;
  created_at: string;
  inactivation_reason?: string | null;
}

/**
 * Display-augmented entry used by this panel. Adds `delta`, computed
 * client-side, since the backend model doesn't return it.
 */
interface RestatementEntry extends RestatementLogEntryDTO {
  delta: number;
}

export function RestatementLogPanel({ reportId, reportData }: RestatementLogPanelProps) {
  const [entries, setEntries] = useState<RestatementEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!reportId) return;
    fetchRestatements();
  }, [reportId]);

  const fetchRestatements = async () => {
    try {
      setLoading(true);
      // Real backend route: GET /api/v1/restatement-log/{report_id} — a
      // path param, not a `?report_id=` query string — returning a BARE
      // LIST of RestatementResponse (see `list_restatements` in
      // backend/app/api/v1/restatement_log.py), not a `{ entries: [...] }`
      // wrapper.
      const response = await api.get<RestatementLogEntryDTO[]>(
        `/api/v1/restatement-log/${reportId}`
      );
      const mapped: RestatementEntry[] = (response || []).map((entry) => ({
        ...entry,
        delta: entry.restated_value - entry.original_value,
      }));
      setEntries(mapped);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch restatements:', err);
      // 404-swallowing BUG (P1 #7): Previously treated 404 as "no restatements
      // yet", hiding legitimate API failures (wrong route, auth issues, etc.).
      // Fixed: Always surface fetch errors so 404s from a miswired backend
      // route fail loudly instead of silently showing empty state.
      setError(err instanceof Error ? err.message : 'Failed to load restatement log');
    } finally {
      setLoading(false);
    }
  };

  const columns: Column<RestatementEntry>[] = [
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      render: (value) => (
        <span className="whitespace-nowrap text-[var(--color-text-secondary)]">
          {new Date(value).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      ),
    },
    {
      key: 'approved_by',
      label: 'Approved By',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-bg-elevated)]">
            <User className="h-3 w-3 text-[var(--color-text-muted)]" />
          </div>
          <p className="text-sm text-[var(--color-text-primary)]">{value || 'Unknown'}</p>
        </div>
      ),
    },
    {
      key: 'field',
      label: 'Field',
      sortable: true,
      render: (value) => (
        <span className="text-sm text-[var(--color-text-secondary)]">{value}</span>
      ),
    },
    {
      key: 'original_value',
      label: 'Previous',
      sortable: true,
      render: (value) => <FinancialValue value={value} />,
    },
    {
      key: 'restated_value',
      label: 'New',
      sortable: true,
      render: (value) => <FinancialValue value={value} />,
    },
    {
      key: 'delta',
      label: 'Delta',
      sortable: true,
      render: (value) => (
        <span className={value >= 0 ? 'text-[#22C55E]' : 'text-[#888888]'}>
          <FinancialValue value={value} />
        </span>
      ),
    },
    {
      key: 'reason',
      label: 'Reason',
      sortable: false,
      render: (value) => (
        <span className="max-w-xs truncate text-sm text-[var(--color-text-muted)]">{value}</span>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        <div className="mb-4 flex items-center gap-3">
          <div className="h-10 w-10 animate-pulse rounded-lg bg-[var(--color-bg-elevated)]" />
          <div className="space-y-2">
            <div className="h-6 w-48 animate-pulse rounded bg-[var(--color-bg-elevated)]" />
            <div className="h-4 w-32 animate-pulse rounded bg-[var(--color-bg-elevated)]" />
          </div>
        </div>
        <div className="h-64 animate-pulse rounded-lg bg-[var(--color-bg-elevated)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        <EmptyState
          title="Failed to Load Restatement Log"
          description={error}
          icon={<AlertTriangle />}
          action={
            <Button onClick={fetchRestatements} variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[rgba(255,102,0,0.12)]">
              <History className="h-6 w-6 text-[var(--color-brand)]" />
            </div>
            <div>
              <h2
                className="text-xl font-semibold text-[var(--color-text-primary)]"
                style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
              >
                Restatement Log
              </h2>
              <p className="text-sm text-[var(--color-text-muted)]">
                Audit trail of all claim amount changes and corrections
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-2xl font-mono font-bold text-[var(--color-text-primary)]">
                {entries.length}
              </p>
              <p className="text-xs text-[var(--color-text-muted)]">Total Restatements</p>
            </div>
            <Button onClick={fetchRestatements} variant="outline" size="sm">
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Restatements Table */}
      <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        {entries.length > 0 ? (
          <DataTable
            columns={columns}
            data={entries}
            emptyMessage="No restatements found"
          />
        ) : (
          <EmptyState
            title="No Restatements"
            description="This report has no recorded restatements. The claim amount has remained consistent since initial calculation."
            icon={<FileText />}
          />
        )}
      </div>

      {/* Summary Stats */}
      {entries.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4">
            <p className="text-xs uppercase tracking-wide text-[var(--color-text-dim)]">
              Net Change
            </p>
            <p
              className={cn(
                'mt-1 text-2xl font-mono font-bold',
                entries.reduce((sum, e) => sum + e.delta, 0) >= 0
                  ? 'text-[#22C55E]'
                  : 'text-[#888888]'
              )}
            >
              <FinancialValue
                value={entries.reduce((sum, e) => sum + e.delta, 0)}
                size="lg"
              />
            </p>
          </div>

          <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4">
            <p className="text-xs uppercase tracking-wide text-[var(--color-text-dim)]">
              Largest Increase
            </p>
            <p className="mt-1 text-2xl font-mono font-bold text-[#22C55E]">
              <FinancialValue
                value={Math.max(...entries.map((e) => e.delta), 0)}
                size="lg"
              />
            </p>
          </div>

          <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4">
            <p className="text-xs uppercase tracking-wide text-[var(--color-text-dim)]">
              Largest Decrease
            </p>
            <p className="mt-1 text-2xl font-mono font-bold text-[#888888]">
              <FinancialValue
                value={Math.min(...entries.map((e) => e.delta), 0)}
                size="lg"
              />
            </p>
          </div>
        </div>
      )}

      {/* Audit Trail Note */}
      <div className="flex items-start gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4">
        <FileText className="h-5 w-5 flex-shrink-0 text-[var(--color-text-dim)]" />
        <div>
          <p className="text-sm font-medium text-[var(--color-text-primary)]">
            Audit Trail Compliance
          </p>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">
            All restatements are permanently recorded with cryptographic signatures
            for forensic defensibility. Each entry includes the previous and new values,
            the author, timestamp, and reason for the change.
          </p>
        </div>
      </div>
    </div>
  );
}

export default RestatementLogPanel;
